import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import MoreVert from "@mui/icons-material/MoreVert";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { useState } from "react";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { ArchiveDialog } from "../../../generic/components/archive-dialog";

import type { FC } from "react";

/** The ⋮ at the end of a row of the feed, to archive it after the home page's "Are you sure?". */
export const RecentlyEditedMenu: FC<{
  title: string;
  label: "Communication" | "Survey" | "Design";
  /** Archives it and says how it went, resolving to whether it was done. */
  archive: () => Promise<boolean>;
  archiving: boolean;
  /** Why it can't be archived yet, if it can't. */
  cannotArchive?: string;
  onArchived: () => void;
}> = ({ title, label, archive, archiving, cannotArchive, onArchived }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = (): void => setAnchor(null);
  const dialog = useDialog();

  const archiveItem = (
    <MenuItem
      disabled={Boolean(cannotArchive) || archiving}
      onClick={() => {
        close();
        dialog.handleOpen();
      }}
      data-analytics-id="dashboard-recently-edited-archive"
    >
      <ListItemIcon>
        <DeleteOutlined fontSize="small" color="error" />
      </ListItemIcon>
      <ListItemText>Archive {label}</ListItemText>
    </MenuItem>
  );

  return (
    <>
      <Tooltip title={`More actions: ${title}`}>
        <IconButton
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          data-analytics-id="dashboard-recently-edited-menu"
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {cannotArchive ? (
          // A disabled item takes no pointer events, so the span shows its tooltip.
          <Tooltip title={cannotArchive}>
            <span>{archiveItem}</span>
          </Tooltip>
        ) : (
          archiveItem
        )}
      </Menu>
      <ArchiveDialog
        open={dialog.open}
        onClose={dialog.handleClose}
        kind={label}
        archiving={archiving}
        analyticsId="dashboard-recently-edited"
        // Stays open, loading, until it's done.
        onConfirm={() => {
          void (async () => {
            try {
              if (await archive()) onArchived();
            } finally {
              dialog.handleClose();
            }
          })().catch(console.error);
        }}
      />
    </>
  );
};
